import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {Input} from 'antd'
import {DeleteOutlined, PlusOutlined} from '@ant-design/icons'
import Heading from 'components/shared/Heading'
import Button from 'components/shared/Button'
import SortableList from 'components/shared/SortableList'

const translations = {
  agentEmail: 'Meglers epost',
  agentName: 'Meglers navn',
  agentPhone: 'Meglers tlf.nr.',
  commonExpences: 'Fellesutgifter',
  estimatedValue: 'Prisantydning',
  lotSize: 'Tomtestørrelse',
  squareMeters: 'Bruksareal (BRA)',
  primaryArea: 'P-Rom',
  viewTimes: 'Visningstid',
}

const MAX_INPUT_LENGTH = 25

const InputForm = ({facts, features, maxFeatures, onSubmit, isSubmitting}) => {
  const [factsState, setFactsState] = useState(facts)
  const [featuresState, setFeaturesState] = useState(features)

  const canAddNewFeature = featuresState.length < maxFeatures

  const handleSubmit = (e) => {
    e.preventDefault()
    return onSubmit({
      facts: factsState,
      features: featuresState,
    })
  }

  const changeFact = (fact, change) => {
    setFactsState(
      factsState.map(f => {
        if (fact.id === f.id) {
          return {...f, ...change}
        }
        return f
      })
    )
  }

  const changeFeature = (feature, change) => {
    setFeaturesState(
      featuresState.map(f => {
        if (feature.id === f.id) {
          return {...f, ...change}
        }
        return f
      })
    )
  }

  const addFeature = () => {
    if (!canAddNewFeature) return

    setFeaturesState([...featuresState, {
      id: Math.random(),
      name: '',
      value: '',
      customizable: true,
    }])
  }

  const removeFeature = (feature) => {
    setFeaturesState(
      featuresState.filter(f => f.id !== feature.id)
    )
  }

  const charsLeft = (value) => {
    return MAX_INPUT_LENGTH - (value || '').length
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="section facts">
        <Heading level={3}>Nøkkelinformasjon</Heading>
        <p>Velg informasjon som skal vises på slutten av videoen.</p>

        {factsState.map((fact) => (
          <div
            key={fact.id}
            className="video-provide-input-form-item form-item"
          >
            <label className="form-item-label">
              {translations[fact.name] || fact.id}
            </label>
            <div className="form-item-control-wrapper">
              <Input
                name={fact.name}
                value={fact.value}
                onChange={e => changeFact(fact, {value: e.target.value})}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="section features">
        <Heading level={3}>Videorekkefølge med tilhørende tekst</Heading>
        <p>Velg rekkefølgen rommene skal vises i videoen, og hvilken tekst som skal stå.</p>

        <SortableList
          labels={featuresState}
          onUpdate={setFeaturesState}
          renderItem={(feature, {dragHandle}) => (
            <div className="video-provide-input-form-item">
              {dragHandle}
              <div className="feature-name">
                {feature.customizable && (
                  <Input
                    name="name"
                    value={feature.name}
                    onChange={e => changeFeature(feature, {name: e.target.value})}
                  />
                )}
                {!feature.customizable && (
                  <label>{feature.name}</label>
                )}
              </div>
              <div className="feature-value">
                <Input
                  name="value"
                  placeholder={feature.placeholder}
                  maxLength={MAX_INPUT_LENGTH}
                  addonAfter={charsLeft(feature.value).toString()}
                  value={feature.value}
                  onChange={e => changeFeature(feature, {value: e.target.value})}
                />
              </div>
              <div className="remove-feature">
                <Button
                  icon={<DeleteOutlined />}
                  onClick={() => removeFeature(feature)}
                />
              </div>
            </div>
          )}
        />

        {canAddNewFeature && (
          <Button
            secondary
            type="action"
            icon={<PlusOutlined />}
            size="small"
            className="add-feature"
            onClick={addFeature}
          >
            Legg til rom
          </Button>
        )}
      </div>

      <Button
        htmlType="submit"
        type="action"
        loading={isSubmitting}
        disabled={isSubmitting}
      >
        Fullfør bestilling
      </Button>
    </form>
  )
}

InputForm.propTypes = {
  maxFeatures: PropTypes.number.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  facts: PropTypes.array,
  features: PropTypes.array,
}

export default InputForm
